import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CheckIcon, EmptyState, ErrorState, LoadingState } from "@signet/ui";
import { fetchTodos, todoKeys, type Show, type Todo } from "./api.ts";
import { TodoDrawer } from "./drawer.tsx";
import { QuickAdd, TodoRow, useMoveTodo, useOpenTodo } from "./row.tsx";
import { useSortable } from "./sortable.ts";
import { StatusMenu, TelegramLink } from "./telegram.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   To-do: the team's shared list (Jay, 2026-10-08). Add one by typing its
   name; open it to give it a creator, priority, due date, person, pin or
   notes. Open shows everything still to do, Mine what is given to you, Done
   what has been ticked off. The open to-do and the view live in the address.
   ────────────────────────────────────────────────────────────────────────── */

const VIEWS: { value: Show; label: string }[] = [
  { value: "open", label: "Everything open" },
  { value: "mine", label: "Given to me" },
  { value: "done", label: "Done" },
];

export function TodoPage() {
  const search = useSearch({ strict: false } as never) as { show?: string; open?: string };
  const navigate = useNavigate();
  const show: Show = search.show === "mine" || search.show === "done" ? search.show : "open";
  const go = (next: { show?: Show; open?: string | null }) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const out: Record<string, unknown> = { ...prev, ...next };
        if (out.show === "open") delete out.show;
        if (!out.open) delete out.open;
        return out;
      }) as never,
      replace: true,
    });

  const list = useQuery({ queryKey: todoKeys.list(show), queryFn: () => fetchTodos(show), refetchInterval: 60_000 });
  const items = list.data?.items ?? [];
  const pinned = items.filter((t) => t.pinned && !t.done);
  const rest = items.filter((t) => !(t.pinned && !t.done));
  const open: Todo | null = useOpenTodo(items, search.open);
  // Each section is its own list to drag in; done ones keep the order they were ticked.
  const move = useMoveTodo(todoKeys.list(show));
  const sortPinned = useSortable({ ids: pinned.map((t) => t.id), groupOf: () => "pinned", onMove: move, disabled: show === "done" });
  const sortRest = useSortable({ ids: rest.map((t) => t.id), groupOf: () => "rest", onMove: move, disabled: show === "done" });

  return (
    <div className="page td-page">
      <header className="page-head">
        <div>
          <h1>To-do</h1>
          <p>The team's shared list. Only the name is needed; open a to-do to add the rest.</p>
        </div>
        <div className="td-head-actions">
          <StatusMenu />
          <TelegramLink />
        </div>
      </header>

      <nav className="td-views" role="tablist" aria-label="Which to-dos">
        {VIEWS.map((v) => (
          <button key={v.value} type="button" role="tab" aria-selected={show === v.value} className={show === v.value ? "on" : ""} onClick={() => go({ show: v.value, open: null })}>
            {v.label}
          </button>
        ))}
      </nav>

      {show !== "done" ? <QuickAdd /> : null}

      {list.isLoading ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState title="Couldn't load the to-dos" onRetry={() => list.refetch()} />
      ) : !items.length ? (
        <EmptyState
          icon={<CheckIcon size={18} />}
          title={show === "done" ? "Nothing ticked off yet" : show === "mine" ? "Nothing is given to you" : "Nothing to do"}
          body={show === "done" ? "Ticked off to-dos show here." : "Type a name above and press Enter to add one."}
        />
      ) : (
        <>
          {pinned.length ? (
            <section className="td-section">
              <h2>Pinned</h2>
              <ul className="td-list">{pinned.map((t) => <TodoRow key={t.id} todo={t} sort={sortPinned} onOpen={() => go({ open: t.id })} />)}</ul>
            </section>
          ) : null}
          {rest.length ? (
            <section className="td-section">
              {pinned.length ? <h2>{show === "done" ? "Done" : "Everything else"}</h2> : null}
              <ul className="td-list">{rest.map((t) => <TodoRow key={t.id} todo={t} sort={sortRest} onOpen={() => go({ open: t.id })} />)}</ul>
            </section>
          ) : null}
        </>
      )}

      <TodoDrawer todo={open} onClose={() => go({ open: null })} />
    </div>
  );
}
